import { type Html, html, View } from 'rune-ts';
import klass from './style/ProductTag.module.scss';
import { Lang } from '../../../../../../types/global';
import { font } from '../../../../shared/typography';
import {
  ITimerSubscribable,
  TimerSubscriptionManager,
  TimerTick,
} from '@services/MarppleShop/shared/util/timer';

export enum ProductTagSize {
  xLarge = 'xLarge',
  large = 'large',
  medium = 'medium',
  small = 'small',
  xSmall = 'xSmall',
}

export enum ProductTagVariant {
  // 배경색 있음
  filled = 'filled',

  // 배경색 없음
  transparent = 'transparent',
}

export enum ProductTagRadius {
  // 알약처럼 완전 동그란
  pill = 'pill',

  // 살짝 동글
  rounded = 'rounded',

  // 없음 (직사각형)
  none = 'none',
}

export enum ProductTagAlign {
  start = 'start',
  center = 'center',
  end = 'end',
}

/**
 * 복수로 적용할 수 있는 옵션
 */
export enum ProductTagOption {
  // 패딩 없이 fit 하게
  fit_content = 'fit_content',

  // width 최대화
  stretch = 'stretch',

  /**
   * 간결한 레이블, 간결한 레이블이 정의되지 않았다면 기본 길이로 보여짐
   */
  compact_label = 'compact_label',
}

export type ProductTagBaseProps = {
  lang: Lang['lang'];
  size: ProductTagSize;

  /**
   * default `filled`
   */
  variant?: ProductTagVariant;

  /**
   * default `pill`
   */
  radius?: ProductTagRadius;

  /**
   * default `start`
   */
  align?: ProductTagAlign;

  options?: ProductTagOption[];
};

export type ProductTagBaseData<T = unknown> = ProductTagBaseProps & {
  value: T;
  now_timestamp: number;
};

export abstract class ProductTagBase<T = unknown>
  extends View<ProductTagBaseData<T>>
  implements ITimerSubscribable
{
  private label?: string;

  private readonly variant: ProductTagVariant;
  private readonly radius: ProductTagRadius;
  private readonly align: ProductTagAlign;
  private readonly options: ProductTagOption[];

  private readonly timerSubscription = new TimerSubscriptionManager(this);

  constructor(data: ProductTagBaseData<T>) {
    super({ ...data });

    this.variant = data.variant ?? ProductTagVariant.filled;
    this.radius = data.radius ?? ProductTagRadius.pill;
    this.align = data.align ?? ProductTagAlign.start;
    this.options = data.options ?? [];

    this.setLabel();
  }

  protected override template() {
    return html`<span
      class="${klass.product_tag}
      ${this.hiddenKlass}
      ${this.sizeKlass}
      ${this.variantKlass}
      ${this.radiusKlass}
      ${this.alignKlass}
      ${this.styles()}
      "
    >
      ${this.isIconVisible ? html`<span class="${this.iconKlass}">${this.getIcon()}</span>` : ''}${this.label}
    </span>`;
  }

  public static readonly Lang: Record<Lang['lang'], string> = {
    kr: 'kr',
    en: 'en',
    jp: 'jp',
  } as const;

  public static readonly Variant: Record<ProductTagVariant, string> = {
    filled: 'filled',
    transparent: 'transparent',
  } as const;

  public static readonly Radius: Record<ProductTagRadius, string> = {
    [ProductTagRadius.pill]: 'pill',
    [ProductTagRadius.rounded]: 'rounded',
    [ProductTagRadius.none]: 'square',
  } as const;

  protected abstract makeLabel(): string | undefined;

  protected makeCompactLabel(): string | undefined {
    return this.makeLabel();
  }

  protected abstract styles(): string;

  private get sizeKlass(): string {
    const sizeKlassMap: Record<ProductTagSize, string[]> = {
      [ProductTagSize.xLarge]: [klass.xLarge, font('14_medium')],
      [ProductTagSize.large]: [klass.large, font('12_medium')],
      [ProductTagSize.medium]: [klass.medium, font('10_medium')],
      [ProductTagSize.small]: [klass.small, font('10_medium')],
      [ProductTagSize.xSmall]: [klass.xSmall, font('10_medium')],
    } as const;

    const klasses: string[] = sizeKlassMap[this.data.size] ?? [];

    if (this.options.includes(ProductTagOption.fit_content)) {
      klasses.push(klass.without_padding);
    }
    if (this.options.includes(ProductTagOption.stretch)) {
      klasses.push(klass.stretch);
    }

    return klasses.filter((k) => k).join(' ');
  }

  private get hiddenKlass(): string {
    return this.label ? '' : klass.hidden;
  }

  private get variantKlass(): string {
    return this.variant === 'transparent' ? klass.transparent : '';
  }

  private get radiusKlass(): string {
    const radiusKlassMap: Record<ProductTagRadius, string[]> = {
      [ProductTagRadius.pill]: [klass.radius, klass.pill],
      [ProductTagRadius.rounded]: [klass.radius, klass.rounded],
      [ProductTagRadius.none]: [klass.radius],
    } as const;

    return radiusKlassMap[this.radius].filter((k) => k).join(' ');
  }

  private get iconKlass(): string {
    const iconKlassMap: Record<ProductTagSize, string[]> = {
      [ProductTagSize.xLarge]: [klass.icon, klass.xLarge],
      [ProductTagSize.large]: [klass.icon, klass.large],
      [ProductTagSize.medium]: [klass.icon, klass.medium],
      [ProductTagSize.small]: [klass.icon, klass.small],
      [ProductTagSize.xSmall]: [klass.icon, klass.xSmall],
    } as const;

    return iconKlassMap[this.data.size].filter((k) => k).join(' ');
  }

  private get alignKlass(): string {
    const alignKlassMap: Record<ProductTagAlign, string[]> = {
      start: [klass.align, klass.start],
      center: [klass.align, klass.center],
      end: [klass.align, klass.end],
    };
    return alignKlassMap[this.align].filter((k) => k).join(' ');
  }

  protected abstract isTimerNeeded(): boolean;

  getIcon(): Html | undefined {
    return undefined;
  }

  private get isIconVisible() {
    return !!this.getIcon();
  }

  protected override onRender(): void {
    super.onRender();
    this.timerSubscription.subscribe();
  }

  protected override onUnmount(): void {
    super.onUnmount();
    this.destroy();
  }

  public setLabel(): this {
    this.label = this.options.includes(ProductTagOption.compact_label)
      ? this.makeCompactLabel()
      : this.makeLabel();
    return this;
  }

  onTimerTick(event: TimerTick): void {
    this.data.now_timestamp = event.detail.timestamp;
    this.setLabel().redraw();
  }

  destroy(): void {
    this.timerSubscription.unsubscribe();
  }
}
